import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'axios';
import FileSaver from 'file-saver';
import { toast } from 'react-toastify';
import { useFieldRefContext } from '../../../../../bitaHooks';
import { URL_CONSTANTS } from '../../../../../api-data-mapping/url-constants';
import IndexSlider from '../components/SliderTickers/IndexSlider';
import { uploadFileMultiple } from '../../../utils/api';
import EditTable from '../../../Components/EditTable';
import { AddInstrumentModal } from '../../../Modals/AddInstrumentModal';
import style from '../../../PortfolioBasketCalculation.module.scss';
import RequestCoverageModal from '../components/RequestCoverageModal';
import BitaButton from '../../../../../bitaComponents/BitaButton/BitaButton';
import BitaModal from '../../../../../bitaComponents/BitaModal/BitaModal';
import BitaCalendarDate from '../../../../../components/BitaCalendar/BitaCalendarDate';
import RequestCoverageSuccessful from '../components/RequestCoverageSuccessful';
import './InitialInputsContent.scss';
import {
  formatDateCalendar,
  validateCalculationType,
  validateCalculationSetup,
  validateLaunchPoint,
  validateCashDistribution,
  validateCurrency,
  hasValidClearStreetAmericas,
  resolveCalculationZone,
} from '../../../../../utils/utils';
import {
  BASKET_STATUS,
  MAX_LAUNCH_DATE,
  TYPE_OF_MODIFICATION,
  REQUEST_COVERAGE_COLUMNS_UBS,
  REQUEST_COVERAGE_COLUMNS_NON_UBS,
  REQUEST_COVERAGE_COLUMN_LABELS,
} from '../../../utils/constants';
import {
  generateEffectiveDate,
  generateLaunchDate,
  generateUniqueSymbol,
  sortMetadataByCoverage,
} from '../../../utils';
import styles from '../components/ParametersForm.module.scss';

const TEMPLATE_FILES_URLS = {
  weights: 'divisor_basket_input_with_open_weight.csv',
  shares: 'divisor_basket_input_with_shares.csv',
  notionals: 'divisor_basket_input_with_notional.csv',
};

const styleClose = {
  position: 'absolute',
  fontSize: '30px',
  fontWeight: 'bold',
  top: '-10px',
  right: '0',
  cursor: 'pointer',
  padding: '10px',
  boxSizing: 'border-box',
};

const getMIC = tokenUser => {
  return get(URL_CONSTANTS.ACE_MIC_ALL_URL, {
    headers: { Authorization: `Bearer ${tokenUser}` },
  });
};

const FileElement = ({ fileName, onRemove }) => (
  <div className="from-metadata--initial-inputs--main-page-container--file-element">
    <div>{fileName}</div>
    <button
      className="from-metadata--initial-inputs--main-page-container--file-element--button"
      onClick={onRemove}
    >
      {' '}
      x
    </button>
  </div>
);

const ClickOrDragComponent = ({
  validateForm,
  setEnableDivisorOrStartingValue,
  authOrgPrefix,
  isSubmitBasket,
  calculationZoneMap,
  addData,
  setSelectedBasketsData,
  allBasketsList,
  selectedBasketsData,
  uploadedFiles,
  setUploadedFiles,
  addPreviousData,
  isUBSUser,
}) => {
  const user = useSelector(state => state.auth);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const isLoading = false;

  const handleDownloadFile = async fileType => {
    FileSaver.saveAs(
      `${process.env.PUBLIC_URL}/files/${TEMPLATE_FILES_URLS[fileType]}`,
      TEMPLATE_FILES_URLS[fileType],
    );
  };

  const handleDragOver = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const uploadFileAndRemoveDuplicates = data => {
    if (!Array.isArray(data) || data.length === 0) return;
    const existingSymbols = (allBasketsList || []).map(basket => basket.symbol?.trim());
    const symbolToFile = new Map();
    const newData = [];
    const prevUploadedSymbols = new Set(
      (selectedBasketsData || []).map(f => f?.symbol?.trim()).filter(Boolean),
    );

    data.forEach(basket => {
      if (basket?.status === BASKET_STATUS.ERROR) {
        toast.error(basket?.message);
        return;
      }

      const baseSymbol =
        basket?.data?.symbol?.trim() ||
        generateUniqueSymbol(
          new Set(existingSymbols),
          symbolToFile,
          prevUploadedSymbols,
          basket?.file_name ?? '',
        );

      const symbol =
        isUBSUser && authOrgPrefix && !baseSymbol.startsWith(authOrgPrefix)
          ? `${authOrgPrefix}${baseSymbol}`
          : baseSymbol;

      // Keep basket symbol normalized so downstream payload/filters use the same value.
      basket.data.symbol = symbol;

      // check duplicated symbols on the previous uploaded files
      if (prevUploadedSymbols.has(symbol)) {
        toast.error(`Basket ${symbol} already exists in previously uploaded files`);
        return;
      }
      // check duplicated symbols on the uploaded files
      if (!symbolToFile.has(symbol)) {
        // check if the basket exists in the baskets list
        if (!isSubmitBasket && !existingSymbols.includes(symbol)) {
          toast.error(`Basket ${symbol} does not exist in the list`);
          return;
        }
        symbolToFile.set(symbol, basket);

        const { launch_date, launch_point, deployment_setup, ...filteredParameters } = basket.data;
        const launchPoint = validateLaunchPoint(
          basket.data?.launch_point || basket.data?.deployment_setup,
          basket.data?.calculation_setup,
          isSubmitBasket,
        );

        const isImmediateLaunchPoint = launchPoint === 'IMMEDIATE' || launchPoint === 'IMMEDIATELY';
        const submitBasketParameters = isSubmitBasket
          ? {
              launch_date: generateLaunchDate(basket.data?.launch_date),
              launch_point: isImmediateLaunchPoint ? 'IMMEDIATE' : launchPoint,
            }
          : {};

        const resolvedCalculationZone = resolveCalculationZone(
          basket.data?.calculation_zone,
          calculationZoneMap,
        );

        newData.push({
          ...filteredParameters,
          ...submitBasketParameters,
          currency: validateCurrency(basket.data?.currency),
          meta_reference_data: sortMetadataByCoverage(basket?.data?.meta_reference_data),
          automatically_adjust_divisor: !basket?.data?.divisor,
          calculation_type: validateCalculationType(basket.data?.calculation_type),
          calculation_setup: validateCalculationSetup(basket.data?.calculation_setup),
          additional_settings: {
            ...basket.data.additional_settings,
            cash_distribution_application_point: basket.data?.application_point,
            m_cash_distribution_type: validateCashDistribution(basket.data.cash_distribution),
            cash_constituent: basket.data.cash_constituent,
          },
          divisor_determination_setup:
            (basket.data?.divisor_determination_setup ||
              basket?.data?.divisor_determination_point) ??
            '',
          effective_date: generateEffectiveDate(basket?.data?.calculation_setup),
          org_prefix: authOrgPrefix,
          calculation_zone: resolvedCalculationZone,
        });
      } else {
        toast.error(`Basket ${symbol} already exists on the file ${basket?.file_name}`);
      }
    });

    newData.forEach(basket => {
      if (isSubmitBasket) {
        // Collect states of divisor and starting value for all baskets, then set once after loop
        const newEnableDivisorOrStartingValue = {};
        newData.forEach(filteredBasket => {
          const existsDivisor = !!filteredBasket?.divisor;
          newEnableDivisorOrStartingValue[filteredBasket?.symbol] = {
            statusDivisor: existsDivisor,
            statusStarting: !existsDivisor,
          };
        });
        setEnableDivisorOrStartingValue(prev => ({ ...prev, ...newEnableDivisorOrStartingValue }));
      }
    });

    if (newData.length > 0) {
      addData(newData);
      addPreviousData(newData);
      validateForm(newData, true);
    }
    const uniqueFiles = Array.from(symbolToFile.values());
    setUploadedFiles(prev => [...prev, ...uniqueFiles]);
  };

  const handleUploadFile = async formData => {
    try {
      const response = await uploadFileMultiple(user.token, formData);
      uploadFileAndRemoveDuplicates(response?.data?.data);
    } catch (error) {
      toast.error(
        'There was an error on the upload process. Please try again later or contact support.',
      );
    }
  };

  const handleDrop = async event => {
    event.preventDefault();
    event.stopPropagation();
    const formData = new FormData();
    const droppedFiles = Array.from(event.dataTransfer.files);
    droppedFiles.forEach(file => formData.append('templates', file));
    handleUploadFile(formData);
  };

  const handleFileUpload = async event => {
    const files = Array.from(event.target.files);
    const formData = new FormData();
    files.forEach(file => formData.append('templates', file));
    handleUploadFile(formData);
  };

  const handleRemoveFile = fileName => {
    setUploadedFiles(prev => prev.filter(item => item.file_name !== fileName));
    setSelectedBasketsData(prev =>
      prev.filter(basket => {
        const symbolInFileName = uploadedFiles.find(item => item.file_name === fileName)?.data
          ?.symbol;
        return basket?.symbol?.trim() !== symbolInFileName?.trim();
      }),
    );
  };

  return (
    <>
      <div
        className={`from-metadata--initial-inputs--main-page-container ${
          isDragging ? 'dragging' : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isLoading ? (
          <>Loading...</>
        ) : (
          <>
            <div className="from-metadata--initial-inputs--top-sub-container--title">
              Click or Drag
              <br />a Basket Metadata File
            </div>

            {uploadedFiles.length === 0 ? (
              <>
                <div className="from-metadata--initial-inputs--top-sub-container--description">
                  Support for single or bulk upload.
                  <br />
                  File format .csv or .xl
                </div>

                <div className="from-metadata--initial-inputs--upload-button-sub-container">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".csv,.xl,.xml"
                    multiple
                    style={{ display: 'none' }}
                  />
                  <button
                    className="from-metadata--initial-inputs--upload-button-sub-container--button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload
                  </button>
                </div>
                <div className="from-metadata--initial-inputs--middle-sub-container">
                  <div className="from-metadata--initial-inputs--middle-sub-container--title">
                    Need a template?
                  </div>
                </div>

                <div className="from-metadata--initial-inputs--bottom-sub-container">
                  <button
                    onClick={() => handleDownloadFile('weights')}
                    className="from-metadata--initial-inputs--bottom-sub-container--button"
                  >
                    Weights
                  </button>

                  <button
                    onClick={() => handleDownloadFile('shares')}
                    className="from-metadata--initial-inputs--bottom-sub-container--button"
                  >
                    Shares
                  </button>

                  <button
                    onClick={() => handleDownloadFile('notionals')}
                    className="from-metadata--initial-inputs--bottom-sub-container--button"
                  >
                    Notionals
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="from-metadata--initial-inputs--main-page-container--files-uploaded">
                  {uploadedFiles.map((fileData, index) => {
                    return (
                      <div key={index}>
                        <FileElement
                          fileName={fileData?.file_name || ''}
                          onRemove={() => handleRemoveFile(fileData?.file_name)}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

const InitialInputsContent = ({
  validateForm,
  errors,
  addPreviousData,
  setEnableDivisorOrStartingValue,
  isSubmitBasket,
  sliderBaskets,
  addData,
  selectedIndex,
  setSelectedIndex,
  setBasketsWithoutCoverage,
  selectedBasketsData,
  setSelectedBasketsData,
  allBasketsList,
  uploadedFiles,
  setUploadedFiles,
  calculationZoneMap,
  isUBSUser,
  resetEditTablePageTrigger,
}) => {
  const user = useSelector(state => state.auth);
  const authOrgPrefix = useSelector(state => state.auth?.org_prefix);
  const { registerFieldRef } = useFieldRefContext();
  const [constituentsDelete, setConstituentsDelete] = useState([]);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [listMIC, setListMIC] = useState([]);
  const [showModalRequestCoverage, setShowModalRequestCoverage] = useState(false);
  const [showModalRequestCoverageSuccessful, setShowModalRequestCoverageSuccessful] = useState(
    false,
  );
  const [selectedIndexUpdatedData, setSelectedIndexUpdatedData] = useState();
  const isClearStreetAmericasUser = hasValidClearStreetAmericas(user?.userdata?.id_company);

  const selectedBasketData = useMemo(
    () => selectedBasketsData?.find(ticker => ticker.symbol === selectedIndex),
    [selectedBasketsData, selectedIndex],
  );

  useEffect(() => {
    setSelectedBasketsData(prevData =>
      prevData.map(basket =>
        basket.symbol === selectedIndexUpdatedData?.symbol ? selectedIndexUpdatedData : basket,
      ),
    );
  }, [selectedIndexUpdatedData]);

  useEffect(() => {
    setSelectedIndexUpdatedData(selectedBasketData);
  }, [selectedIndex, selectedBasketData]);

  useEffect(() => {
    getMIC(user.token)
      .then(rsp => {
        setListMIC(Object.values(rsp?.data?.data));
      })
      .catch(error => {
        toast.error('Error fetching MIC list');
      });
  }, []);

  const handleConstituentToggle = value => {
    setConstituentsDelete(prev => {
      const exists = constituentsDelete.some(
        item => item.isin === value?.isin || (!value?.isin && item.sedol === value?.sedol),
      );
      if (exists) {
        return prev.filter(
          item =>
            !(
              (value.isin && item.isin === value.isin) ||
              (!value?.isin && value.sedol && item.sedol === value.sedol)
            ),
        );
      }
      return [...prev, value];
    });
  };
  const constituentsNeedCoverage = useMemo(() => {
    return (selectedIndexUpdatedData || {}).meta_reference_data?.filter(
      item => item?.type !== TYPE_OF_MODIFICATION.WITH_COVERAGE,
    );
  }, [selectedIndexUpdatedData]);

  useEffect(() => {
    if (constituentsNeedCoverage?.length > 0) {
      setBasketsWithoutCoverage(prev =>
        prev.includes(selectedIndex) ? prev : [...prev, selectedIndex],
      );
    } else {
      setBasketsWithoutCoverage(prev => prev.filter(item => item !== selectedIndex));
    }
  }, [constituentsNeedCoverage]);

  const filteredColumns = useMemo(() => {
    return Object.keys(selectedBasketData?.meta_reference_data?.[0] ?? {}).filter(
      key =>
        ![
          ...(selectedBasketData?.identifier_type === 'isin'
            ? ['isin', 'sedol']
            : ['sedol', 'isin']),
          'ticker',
          'company_name',
          'exchange',
          'shares',
          'type',
        ].includes(key),
    );
  }, [selectedBasketsData, selectedIndex]);

  const handleEffectiveDate = value => {
    setSelectedBasketsData(prev =>
      prev.map(basket =>
        basket.symbol === selectedIndex
          ? { ...basket, effective_date: formatDateCalendar(value) }
          : basket,
      ),
    );
  };

  const handleRemoveBasket = () => {
    setUploadedFiles(prev => prev.filter(file => file?.data?.symbol !== selectedIndex));
    setSelectedBasketsData(prev => prev.filter(basket => basket.symbol !== selectedIndex));
    setSelectedIndex(
      selectedBasketsData.length > 0
        ? selectedBasketsData.find(basket => basket.symbol !== selectedIndex)?.symbol ?? null
        : null,
    );
    // Remove the selected basket from the basketsWithoutCoverage list as well
    setBasketsWithoutCoverage(prev => prev.filter(basketIndex => basketIndex !== selectedIndex));
    setEnableDivisorOrStartingValue(prev => {
      const newState = { ...prev };
      delete newState[selectedIndex];
      return newState;
    });
    toast.success(`Basket ${selectedIndex} removed successfully`);
  };

  return (
    <>
      <div className="from-metadata--initial-inputs--tab-container">
        <div className="from-metadata--initial-inputs--section-title">INITIAL INPUTS</div>
        <ClickOrDragComponent
          validateForm={validateForm}
          addPreviousData={addPreviousData}
          setEnableDivisorOrStartingValue={setEnableDivisorOrStartingValue}
          authOrgPrefix={authOrgPrefix}
          isSubmitBasket={isSubmitBasket}
          calculationZoneMap={calculationZoneMap}
          addData={addData}
          setSelectedBasketsData={setSelectedBasketsData}
          selectedBasketsData={selectedBasketsData}
          allBasketsList={allBasketsList}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          isUBSUser={isUBSUser}
        />

        {selectedBasketsData?.[0]?.meta_reference_data && (
          <div
            className="from-metadata--initial-inputs--table-container"
            ref={registerFieldRef('metadata_table_container')}
          >
            <div className="from-metadata--initial-inputs--table-container--indexes-list">
              <IndexSlider
                indexes={sliderBaskets ?? []}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
              />
            </div>

            <div className="from-metadata--initial-inputs--table-container--title">
              UPLOADED METADATA LIST
            </div>

            <div className="from-metadata--initial-inputs--table-container--table">
              {!isSubmitBasket && selectedBasketData?.meta_reference_data && (
                <div
                  className={styles.effectiveDateContainer}
                  id="field-effective_date"
                  ref={registerFieldRef('effective_date')}
                >
                  <BitaCalendarDate
                    title="Effective Date"
                    select={value => handleEffectiveDate(value)}
                    maxDate={MAX_LAUNCH_DATE}
                    minDate={generateEffectiveDate(selectedBasketData?.calculation_setup)}
                    date={selectedBasketData?.effective_date}
                  />
                  {errors[selectedIndex]?.effective_date && (
                    <div
                      className={`${styles.errorMessageStaticPosition} ${styles.marginTopSmall}`}
                    >
                      {errors[selectedIndex]?.effective_date}
                    </div>
                  )}
                </div>
              )}
              {selectedBasketData?.meta_reference_data && (
                <EditTable
                  basket={selectedIndexUpdatedData}
                  rows={selectedIndexUpdatedData?.meta_reference_data || []}
                  selectedIndex={selectedIndex}
                  listMic={listMIC}
                  setMetadata={setSelectedIndexUpdatedData}
                  hiddenColumn={!isClearStreetAmericasUser ? filteredColumns : ['sedol']}
                  constituentsDelete={constituentsDelete}
                  setConstituentsDelete={value => handleConstituentToggle(value)}
                  allDelete={value => {
                    setConstituentsDelete(value);
                  }}
                  setErrorReferenceData={() => {}}
                  tableId="initial-inputs-table"
                  registerFieldRef={registerFieldRef}
                  resetToPageOneTrigger={resetEditTablePageTrigger}
                />
              )}

              <div className="from-metadata--initial-inputs--table-container--table--buttons-container">
                <button
                  className="bulk-basket-button bulk-basket-request-remove-basket-button"
                  onClick={handleRemoveBasket}
                >
                  Remove Basket
                </button>

                <button
                  className="bulk-basket-button bulk-update-standard-button"
                  disabled={constituentsDelete.length === 0}
                  onClick={() => {
                    setShowModalDelete(true);
                  }}
                >
                  Delete Instrument
                </button>

                <button
                  className="bulk-basket-button bulk-update-standard-button"
                  onClick={() => setShowModalAdd(true)}
                >
                  Add Instrument
                </button>

                <button
                  className={`bulk-basket-button bulk-update-standard-button ${
                    constituentsNeedCoverage?.length === 0 ? 'request-coverage-button-disabled' : ''
                  }`}
                  onClick={() => setShowModalRequestCoverage(true)}
                >
                  Request Coverage
                </button>
              </div>
            </div>
          </div>
        )}
        <AddInstrumentModal
          listMic={listMIC}
          isOpen={showModalAdd}
          cashOption={
            !selectedBasketsData
              ?.find(ticker => ticker.symbol === selectedIndex)
              ?.meta_reference_data?.find(item => item?.isin?.includes('CASH'))
          }
          onClose={() => setShowModalAdd(false)}
          listInstruments={selectedBasketData?.meta_reference_data}
          addInstrument={newInstrument => {
            const updatedData = selectedBasketsData?.map(basket =>
              basket.symbol === selectedIndex
                ? {
                    ...basket,
                    meta_reference_data: [newInstrument, ...basket.meta_reference_data],
                    ...(newInstrument.exchange === 'CASH'
                      ? { cash_constituent: { interest_rate_on_cash: null } }
                      : {}),
                  }
                : basket,
            );
            setSelectedIndexUpdatedData(updatedData.find(item => item.symbol === selectedIndex));
            setSelectedBasketsData(updatedData);
          }}
        />
      </div>

      {/* delete constituents modal Modal */}
      <BitaModal isModalOpen={showModalDelete} setModalState={() => setShowModalDelete(false)}>
        <div style={{ width: '600px ', minHeight: '380px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
              padding: '10px 0',
            }}
          >
            <h2
              style={{
                margin: '0',
                padding: '0',
                fontSize: '20px',
                textTransform: 'uppercase',
                textAlign: 'center',
                marginLeft: '-20px',
                width: '100%',
              }}
            >
              Delete Constituents
            </h2>
            <span onClick={() => setShowModalDelete(false)} style={styleClose}>
              &times;
            </span>
          </div>
          <div>
            <div style={{ margin: '20px', textAlign: 'center' }}>
              Do you want to remove these constituents?
            </div>

            <div style={{ height: '360px' }}>
              <div style={{ width: '100%' }}>
                <div>Constituents</div>
                <div className={style.contentNoMetadata} style={{ width: '100%' }}>
                  <div className={style.tableNoMetadata}>
                    <div className={style.requestCoverageEntry}>
                      <>
                        {(isUBSUser
                          ? REQUEST_COVERAGE_COLUMNS_UBS
                          : REQUEST_COVERAGE_COLUMNS_NON_UBS
                        ).map(key => (
                          <div key={key}>{REQUEST_COVERAGE_COLUMN_LABELS[key]}</div>
                        ))}
                      </>
                    </div>
                    {(constituentsDelete || []).map(item => (
                      <div key={item?.isin ?? item?.sedol} className={style.requestCoverageEntry}>
                        <>
                          {(isUBSUser
                            ? REQUEST_COVERAGE_COLUMNS_UBS
                            : REQUEST_COVERAGE_COLUMNS_NON_UBS
                          ).map(key => (
                            <div key={key}>{item?.[key] ?? '-'}</div>
                          ))}
                        </>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <BitaButton
                onClick={() => {
                  setShowModalDelete(false);
                  const arrayIdentifier = constituentsDelete.map(item => item.isin ?? item.sedol);
                  const basket = selectedIndexUpdatedData;

                  const meta_reference_data = [
                    ...basket.meta_reference_data?.filter(
                      item => !arrayIdentifier?.includes(item?.isin ?? item?.sedol),
                    ),
                  ];
                  const updatedElement = {
                    ...basket,
                    meta_reference_data,
                  };
                  const updatedData = [
                    updatedElement,
                    ...selectedBasketsData.filter(
                      itemBasket => itemBasket.symbol !== selectedIndex,
                    ),
                  ];
                  setSelectedIndexUpdatedData(updatedElement);
                  setSelectedBasketsData(updatedData);
                  setConstituentsDelete([]);
                }}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
                autofocus
              >
                Delete
              </BitaButton>
              <BitaButton
                onClick={() => setShowModalDelete(false)}
                width="150px"
                height="30px"
                style={{ margin: '5px' }}
                primary
              >
                Cancel
              </BitaButton>
            </div>
          </div>
        </div>
      </BitaModal>
      <RequestCoverageModal
        isUBSUser={isUBSUser}
        data={selectedBasketsData}
        user={user}
        showModalRequestCoverage={showModalRequestCoverage}
        setShowModalRequestCoverage={setShowModalRequestCoverage}
        setShowModalRequestCoverageSuccessful={setShowModalRequestCoverageSuccessful}
      />
      <RequestCoverageSuccessful
        showModalRequestCoverageSuccessful={showModalRequestCoverageSuccessful}
        setShowModalRequestCoverageSuccessful={setShowModalRequestCoverageSuccessful}
      />
    </>
  );
};

export default InitialInputsContent;
